import type { AxiosInstance } from "axios";
import { ChevronRight, Layers, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card } from "../components/ui/card.js";
import { DateRangePicker } from "../components/DateRangePicker.js";
import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Select } from "../components/ui/select.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import { formatCount } from "../lib/format.js";
import { presetRange, rangeProblem, type DateRange } from "../lib/dates.js";

/**
 * Latent Value Analysis. Ported from `ecom_code`'s `LatentValue.jsx` +
 * `latentValue.query.js`/`.service.js` — same cohort logic, same trend-line + interval-
 * table shape, same "View by Source / View by Campaign" toggle with a Meta/Google/All
 * platform filter and a top-5-by-default campaign multi-select. Table/type scale/colour
 * TOKENS are this platform's own; the report's own defining visual — the cumulative
 * purchaser-growth line chart, and the crosshatch-free but still brand-coloured legend —
 * is ported, not dropped, per the same owner correction Campaign Stage Analysis's own
 * stacked bar got: this platform had no chart library before this report, so `recharts`
 * (the exact library legacy uses) is now a dependency of `apps/web`.
 *
 * ORDERS COLUMNS ARE NOT RENDERED — legacy's own `LatentValue.jsx` comments its "Orders"
 * table column out (`/* ... *\/` around lines 741-760) and its custom tooltip only ever
 * shows Visitors + Purchasers. The API still returns order counts (parity with the SQL),
 * this screen just doesn't have a column for them, matching legacy's ACTUAL rendered UI
 * rather than its dead code.
 */

interface LatentValueSource {
  readonly channel: string;
  readonly channel_name: string;
  readonly total_new_visitors: number;
  readonly d0: number;
  readonly d7: number;
  readonly d14: number;
  readonly d30: number;
  readonly d60: number;
  readonly d90: number;
  readonly d180: number;
  readonly d7_p_inc: number;
  readonly d14_p_inc: number;
  readonly d30_p_inc: number;
  readonly d60_p_inc: number;
  readonly d90_p_inc: number;
  readonly d180_p_inc: number;
}

interface LatentValueCampaign extends LatentValueSource {
  readonly campaign_id: string;
  readonly campaign_name: string;
}

interface LatentValueResponse {
  readonly from: string;
  readonly to: string;
  readonly sources: readonly LatentValueSource[];
  readonly campaigns: readonly LatentValueCampaign[];
}

type GroupBy = "source" | "campaign";
type PlatformFilter = "all" | "meta" | "google";
type SortDirection = "desc" | "asc";

const MILESTONES: readonly { readonly key: "d0" | "d7" | "d14" | "d30" | "d60" | "d90" | "d180"; readonly label: string; readonly fullLabel: string }[] = [
  { key: "d0", label: "D0", fullLabel: "Day 0" },
  { key: "d7", label: "D7", fullLabel: "Day 7" },
  { key: "d14", label: "D14", fullLabel: "Day 14" },
  { key: "d30", label: "D30", fullLabel: "Day 30" },
  { key: "d60", label: "D60", fullLabel: "Day 60" },
  { key: "d90", label: "D90", fullLabel: "Day 90" },
  { key: "d180", label: "D180", fullLabel: "Day 180" },
];

/** Interval (non-cumulative) purchasers for a milestone — D0 has no "before it" to subtract. */
function intervalPurchasers(entry: LatentValueSource, key: (typeof MILESTONES)[number]["key"]): number {
  if (key === "d0") return entry.d0;
  return entry[`${key}_p_inc`];
}

// Ported verbatim from legacy's own palette — distinct, high-contrast series colours.
const LINE_COLORS: readonly string[] = [
  "#d946ef", "#ef4444", "#2563eb", "#f97316", "#06b6d4", "#10b981", "#8b5cf6", "#eab308", "#4f46e5", "#059669",
];
const SOURCE_BRAND_COLORS: Readonly<Record<string, string>> = {
  meta: "#d946ef",
  google: "#2563eb",
  "google-organic": "#ef4444",
  "meta-organic": "#a855f7",
  direct: "#f97316",
  gokwik: "#10b981",
  kwikengage: "#06b6d4",
  "chatgpt.com": "#059669",
  judgeme: "#8b5cf6",
};

function seriesColor(name: string, index: number): string {
  const normalized = name.toLowerCase().trim();
  return SOURCE_BRAND_COLORS[normalized] ?? LINE_COLORS[index % LINE_COLORS.length] ?? "#64748b";
}

function defaultLatentRange(today: Date = new Date()): DateRange {
  return presetRange("last28", today);
}

export function LatentValueAnalysisScreen({
  client,
  tenantId,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly storeName: string;
}): React.JSX.Element {
  const [range, setRange] = useState<DateRange>(() => defaultLatentRange());
  const [data, setData] = useState<LatentValueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGuideSection, setShowGuideSection] = useState(false);
  const { colWidths, startResize } = useTableResize([280, 180, 160, 150]);

  const [groupBy, setGroupBy] = useState<GroupBy>("source");
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<ReadonlySet<string>>(new Set());
  const [isUserCustomized, setIsUserCustomized] = useState(false);
  const [expandedEntities, setExpandedEntities] = useState<ReadonlySet<string>>(new Set());
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const problem = useMemo(() => rangeProblem(range), [range]);

  const load = useCallback(async () => {
    if (problem !== null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<{ success: boolean; data: LatentValueResponse }>("/api/v2/latent-value-analysis", {
        params: { from: range.from, to: range.to },
      });
      setData(res.data.data);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load latent value analysis. Check that an ad platform is connected."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client, range.from, range.to, problem]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  // Available campaigns for the active platform filter, deduplicated by id — same shape
  // legacy's `availableCampaigns` memo builds.
  const availableCampaigns = useMemo(() => {
    const list = data?.campaigns ?? [];
    const filtered = platformFilter === "all" ? list : list.filter((c) => c.channel === platformFilter);
    const seen = new Set<string>();
    const result: LatentValueCampaign[] = [];
    for (const c of filtered) {
      if (c.campaign_id !== "" && !seen.has(c.campaign_id)) {
        seen.add(c.campaign_id);
        result.push(c);
      }
    }
    return result;
  }, [data?.campaigns, platformFilter]);

  // Fresh top-5 selection whenever new data or a new platform filter arrives, unless the
  // owner has manually customised the selection — matches legacy's `fetchData`/
  // `handlePlatformChange` reset logic.
  useEffect(() => {
    if (isUserCustomized) {
      setSelectedCampaignIds((prev) => {
        const stillValid = [...prev].filter((id) => availableCampaigns.some((c) => c.campaign_id === id));
        return stillValid.length > 0 ? new Set(stillValid) : new Set(availableCampaigns.slice(0, 5).map((c) => c.campaign_id));
      });
      return;
    }
    setSelectedCampaignIds(new Set(availableCampaigns.slice(0, 5).map((c) => c.campaign_id)));
    // `isUserCustomized` is deliberately not a dependency: toggling it (via
    // `handlePlatformChange`/`toggleCampaignSelected`) must not, by itself, re-run this
    // effect — only a NEW `availableCampaigns` list should.
  }, [availableCampaigns]);

  const handlePlatformChange = (value: PlatformFilter): void => {
    setPlatformFilter(value);
    setIsUserCustomized(false);
  };

  const toggleCampaignSelected = (campaignId: string): void => {
    setIsUserCustomized(true);
    setSelectedCampaignIds((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  };

  const toggleExpanded = (entityId: string): void => {
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) next.delete(entityId);
      else next.add(entityId);
      return next;
    });
  };

  // The active entity list — sources, or the selected campaigns — sorted by New Visitors.
  const activeEntities = useMemo(() => {
    const raw: (LatentValueSource & { readonly entityId: string; readonly displayName: string })[] =
      groupBy === "source"
        ? (data?.sources ?? []).map((s) => ({ ...s, entityId: s.channel, displayName: s.channel_name }))
        : availableCampaigns
            .filter((c) => selectedCampaignIds.has(c.campaign_id))
            .map((c) => ({ ...c, entityId: c.campaign_id, displayName: c.campaign_name }));

    return [...raw].sort((a, b) =>
      sortDirection === "desc" ? b.total_new_visitors - a.total_new_visitors : a.total_new_visitors - b.total_new_visitors,
    );
  }, [groupBy, data?.sources, availableCampaigns, selectedCampaignIds, sortDirection]);

  const chartSeries = useMemo(
    () => activeEntities.map((entity, index) => ({ entityId: entity.entityId, name: entity.displayName, color: seriesColor(entity.displayName, index) })),
    [activeEntities],
  );

  const chartPoints = useMemo(
    () =>
      MILESTONES.map((m) => {
        const point: Record<string, number | string> = { dayLabel: m.label };
        for (const entity of activeEntities) point[entity.entityId] = entity[m.key];
        return point;
      }),
    [activeEntities],
  );

  const isEmpty = !loading && activeEntities.length === 0;

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <TrendingUp className="text-primary size-6" />
            Latent Value Analysis
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
            />
          </h1>
          <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
            How much of an acquisition channel&apos;s payoff arrives later — cumulative purchaser growth over 180 days.
          </p>
        </div>

        <DateRangePicker value={range} onApply={setRange} />
      </header>

      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatContent={
          <div className={noteText}>
            <p className="m-0">
              Latent Value Analysis reveals how much of your acquisition marketing payoff arrives{" "}
              <strong>later in time</strong> rather than on the initial visit:
            </p>
            <HighlightGrid>
              <HighlightCard tone="blue" title="1. Trend graph (cumulative progression)" icon={<TrendingUp className="size-3.5" />}>
                Visualises the cumulative maturation of purchasers over 180 days (D0 → D7 → D14 → D30 → D60 → D90 →
                D180) — each point is the total running count of buyers acquired from that cohort, up to that day.
              </HighlightCard>
              <HighlightCard tone="purple" title="2. Performance table (interval breakdown)" icon={<Layers className="size-3.5" />}>
                Shows the incremental activity inside each interval: Day 0 is the fixed pool of New Visitors plus
                same-day buyers; Days 7–180 show the additional new buyers who converted specifically in that
                interval.
              </HighlightCard>
            </HighlightGrid>
          </div>
        }
        howTitle="How cohorts & date ranges are calculated"
        howContent={
          <div className={noteText}>
            <p className="m-0">How the cohort and its milestones are calculated:</p>
            <FormulaList>
              <li>
                <strong>The cohort:</strong> every visitor whose first-ever visit to the store falls inside the
                selected date range — the same "new visitor" definition Campaign Stage Analysis and Cohort LTV use.
              </li>
              <li>
                <strong>Acquisition channel/campaign:</strong> read from each cohort visitor&apos;s EARLIEST session
                ever, not just a session inside the window.
              </li>
              <li>
                <strong>Day 0 → Day 180:</strong> days elapsed since each visitor&apos;s OWN cohort date — not a fixed
                calendar date. A cohort of several days is aggregated into one combined timeline.
              </li>
              <li>
                <strong>Cumulative vs interval:</strong> the graph shows the running total of purchasers by each
                milestone; the table shows only the NEW purchasers who converted inside that specific interval.
              </li>
            </FormulaList>
          </div>
        }
      />

      {problem !== null && (
        <p className="text-destructive text-sm" role="alert">
          {problem}
        </p>
      )}
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      <Card className="gap-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">Group by</span>
            <Select
              value={groupBy}
              onChange={(e) => {
                setGroupBy(e.target.value as GroupBy);
                setExpandedEntities(new Set());
              }}
              className="h-9 w-44 text-xs font-medium"
              aria-label="Group by"
            >
              <option value="source">View by Source</option>
              <option value="campaign">View by Campaign</option>
            </Select>
          </div>

          {groupBy === "campaign" && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">Platform</span>
                <Select
                  value={platformFilter}
                  onChange={(e) => handlePlatformChange(e.target.value as PlatformFilter)}
                  className="h-9 w-32 text-xs font-medium"
                  aria-label="Platform filter"
                >
                  <option value="all">All Platforms</option>
                  <option value="meta">Meta</option>
                  <option value="google">Google</option>
                </Select>
              </div>
              <CampaignMultiSelect
                campaigns={availableCampaigns}
                selected={selectedCampaignIds}
                onToggle={toggleCampaignSelected}
              />
            </div>
          )}
        </div>

        {loading ? (
          <Skeleton className="h-[420px] w-full" />
        ) : chartSeries.length === 0 ? (
          <div className="text-muted-foreground py-20 text-center text-sm">
            No purchaser data found for the selected date range.
          </div>
        ) : (
          <div style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height={420}>
              <LineChart data={chartPoints} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="dayLabel" tick={{ fontSize: 12, fontWeight: 600 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  label={{ value: "Cumulative purchasers", angle: -90, position: "insideLeft", fontSize: 12, fontWeight: 600 }}
                />
                <RechartsTooltip content={<LatentValueChartTooltip series={chartSeries} />} isAnimationActive={false} />
                <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 16, fontSize: 12 }} />
                {chartSeries.map((s) => (
                  <Line
                    key={s.entityId}
                    type="monotone"
                    dataKey={s.entityId}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2.5}
                    dot={{ r: 3, strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        <div className="border-b px-5 py-3.5">
          <h2 className="text-sm font-bold">Cohort Performance Breakdown — Interval Purchasers</h2>
        </div>
        <div className="max-h-[min(70svh,48rem)] overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="row-head text-foreground sticky top-0 left-0 z-30 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative">
                  {groupBy === "source" ? "Channel" : "Campaign Name"}
                  <TableResizeHandle onResize={(e) => startResize(0, e)} />
                </th>
                <ColHead onResize={(e) => startResize(1, e)}>Days since first touch</ColHead>
                <th
                  className="row-head text-foreground sticky top-0 z-20 h-11 cursor-pointer border-b border-r border-border px-3 text-center text-[13px] font-semibold select-none relative"
                  onClick={() => setSortDirection((d) => (d === "desc" ? "asc" : "desc"))}
                >
                  <span className="inline-flex items-center gap-1">
                    New Visitors
                    <ChevronRight className={cn("size-3 transition-transform", sortDirection === "desc" ? "rotate-90" : "-rotate-90")} />
                  </span>
                  <TableResizeHandle onResize={(e) => startResize(2, e)} />
                </th>
                <ColHead onResize={(e) => startResize(3, e)}>Purchasers</ColHead>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }, (_, i) => (
                  <tr key={i} className={i % 2 === 1 ? "row-odd" : "row-even"}>
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-4 w-40" />
                    </td>
                    <td className="border-b px-3 py-3 text-center">
                      <Skeleton className="mx-auto h-4 w-16" />
                    </td>
                    <td className="border-b px-3 py-3 text-center">
                      <Skeleton className="mx-auto h-4 w-12" />
                    </td>
                    <td className="border-b px-3 py-3 text-center">
                      <Skeleton className="mx-auto h-4 w-12" />
                    </td>
                  </tr>
                ))
              ) : isEmpty ? (
                <tr>
                  <td colSpan={4} className="text-muted-foreground px-4 py-14 text-center text-sm">
                    No cohort data found for {storeName} in this period{groupBy === "campaign" ? " and campaign selection" : ""}.
                  </td>
                </tr>
              ) : (
                activeEntities.map((entity, idx) => (
                  <EntityRows
                    key={entity.entityId}
                    entity={entity}
                    color={seriesColor(entity.displayName, idx)}
                    isExpanded={expandedEntities.has(entity.entityId)}
                    onToggle={() => toggleExpanded(entity.entityId)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ColHead({
  children,
  onResize,
}: {
  readonly children: React.ReactNode;
  readonly onResize?: (event: React.MouseEvent) => void;
}): React.JSX.Element {
  return (
    <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-center text-[13px] font-semibold relative">
      <span className="pr-1">{children}</span>
      {onResize && <TableResizeHandle onResize={onResize} />}
    </th>
  );
}

function EntityRows({
  entity,
  color,
  isExpanded,
  onToggle,
}: {
  readonly entity: LatentValueSource & { readonly entityId: string; readonly displayName: string };
  readonly color: string;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  const visibleMilestones = isExpanded ? MILESTONES : MILESTONES.slice(0, 1);

  return (
    <>
      {visibleMilestones.map((m) => {
        const isDay0 = m.key === "d0";
        return (
          <tr key={m.key} className={isDay0 ? "bg-card border-t" : "row-odd"}>
            <td className="border-b px-3 py-2.5">
              {isDay0 ? (
                <button type="button" onClick={onToggle} className="flex items-center gap-2 text-left">
                  <ChevronRight className={cn("text-muted-foreground size-3.5 shrink-0 transition-transform", isExpanded && "rotate-90")} />
                  <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-foreground truncate text-xs font-bold uppercase">{entity.displayName}</span>
                </button>
              ) : (
                <div className="flex items-center gap-2 pl-7">
                  <span className="text-muted-foreground/60 size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color, opacity: 0.4 }} />
                  <span className="text-muted-foreground truncate text-xs font-semibold uppercase opacity-60">{entity.displayName}</span>
                </div>
              )}
            </td>
            <td className={cn("border-b px-3 py-2.5 text-center text-xs", isDay0 ? "text-foreground font-semibold" : "text-muted-foreground font-medium")}>
              {m.fullLabel}
            </td>
            <td className="border-b px-3 py-2.5 text-center text-xs tabular-nums">
              {isDay0 ? (
                <strong className="text-foreground font-bold">{formatCount(entity.total_new_visitors)}</strong>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className={cn("border-b px-3 py-2.5 text-center text-xs font-semibold tabular-nums", isDay0 ? "text-primary" : "text-primary/80")}>
              {formatCount(intervalPurchasers(entity, m.key))}
            </td>
          </tr>
        );
      })}
    </>
  );
}

interface ChartSeriesInfo {
  readonly entityId: string;
  readonly name: string;
  readonly color: string;
}

interface ChartTooltipEntry {
  readonly dataKey?: string | number;
  readonly value?: number;
  readonly color?: string;
  readonly name?: string;
}

/** The custom hover tooltip on the trend line — ported from legacy's `CustomTooltip`. */
function LatentValueChartTooltip({
  active,
  label,
  payload,
  series,
}: {
  readonly active?: boolean;
  readonly label?: string;
  readonly payload?: readonly ChartTooltipEntry[];
  readonly series: readonly ChartSeriesInfo[];
}): React.JSX.Element | null {
  if (active !== true || payload === undefined || payload.length === 0) return null;

  const sorted = [...payload].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  return (
    <div className="bg-popover text-popover-foreground min-w-[280px] rounded-xl border p-3 text-xs shadow-lg">
      <div className="mb-1.5 flex items-center justify-between border-b pb-1.5 font-semibold">
        <span className="uppercase tracking-wide">Milestone: {label}</span>
      </div>
      <div className="space-y-1">
        {sorted.map((entry) => {
          const info = series.find((s) => s.entityId === entry.dataKey);
          return (
            <div key={String(entry.dataKey)} className="bg-muted/60 flex items-center justify-between gap-3 rounded-md px-2 py-1">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="truncate font-semibold" title={info?.name}>
                  {info?.name ?? entry.name}
                </span>
              </span>
              <span className="text-primary shrink-0 font-bold tabular-nums">{formatCount(entry.value ?? 0)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A small checklist popover — the platform has no multi-select control yet, and no other
 * report needs one (per .ai/04, a shared component is extracted the moment a SECOND
 * caller needs it, not the first). `createPortal`/`position: fixed`, same technique as
 * `AppSidebar.tsx`'s `GroupFlyout` and `CampaignStageAnalysisScreen.tsx`'s bar tooltip.
 */
function CampaignMultiSelect({
  campaigns,
  selected,
  onToggle,
}: {
  readonly campaigns: readonly LatentValueCampaign[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (campaignId: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) === true) return;
      if (panelRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  const toggleOpen = (): void => {
    if (!open) {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect !== undefined) {
        // The panel is `w-80` (320px). Anchoring it at the trigger's own left edge
        // overflows off-screen whenever the trigger sits in the right half of a wide
        // toolbar (exactly what happened here) — so it aligns to the trigger's RIGHT
        // edge instead, then clamps into the viewport with an 8px margin on both sides.
        const PANEL_WIDTH = 320;
        const MARGIN = 8;
        const preferredLeft = rect.right - PANEL_WIDTH;
        const left = Math.min(Math.max(preferredLeft, MARGIN), window.innerWidth - PANEL_WIDTH - MARGIN);
        setCoords({ top: rect.bottom + 6, left });
      }
    }
    setOpen((prev) => !prev);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">Campaigns</span>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        className="border-input hover:bg-accent flex h-9 min-w-[220px] items-center justify-between rounded-md border bg-transparent px-3 text-xs font-medium shadow-xs"
      >
        <span className="truncate">{selected.size === 0 ? "Select campaigns..." : `${String(selected.size)} campaign${selected.size === 1 ? "" : "s"} selected`}</span>
      </button>

      {open &&
        coords !== null &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            style={{ position: "fixed", top: coords.top, left: coords.left }}
            className="bg-popover text-popover-foreground z-50 max-h-72 w-80 overflow-auto rounded-xl border p-1.5 shadow-lg"
          >
            {campaigns.length === 0 ? (
              <div className="text-muted-foreground px-2.5 py-2 text-xs">No campaigns for this platform.</div>
            ) : (
              campaigns.map((c) => {
                const isSelected = selected.has(c.campaign_id);
                return (
                  <button
                    key={c.campaign_id}
                    type="button"
                    onClick={() => onToggle(c.campaign_id)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs",
                      isSelected ? "bg-accent" : "hover:bg-accent/60",
                    )}
                  >
                    <span
                      className={cn(
                        "flex size-3.5 shrink-0 items-center justify-center rounded-xs border",
                        isSelected ? "bg-primary border-primary" : "border-input",
                      )}
                    >
                      {isSelected && <span className="bg-primary-foreground block size-1.5 rounded-xs" />}
                    </span>
                    <span className="truncate">
                      {c.campaign_name} <span className="text-muted-foreground">({c.channel.toUpperCase()})</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
